/**
 * /operations/hub — HR Operations Hub landing page (M30.5.1).
 *
 * Consumes the M30 P8 aggregator (`GET /api/operations/hub`) and
 * renders the 7-card grid HR uses to initiate transactions on
 * behalf of employees. Each card shows:
 *   - the bilingual transaction-type label (from the aggregator)
 *   - pending count (transactions in approval workflow)
 *   - recent commit count (last 7 days)
 *   - two CTAs: "Create new" → the HR-initiate form, "View list"
 *     → the existing employee-initiated detail page
 *
 * The EOS card carries a "Coming M30.6" badge per Amer's M30.5
 * refinement #2 — counts + aggregator math work today (backend P6
 * shipped), but the HR-initiate form is deferred to M30.6 because
 * EOS deserves its own focused sprint (Article 80 forfeiture
 * checklist, beneficiary flow, gov filings).
 *
 * Below the grid:
 *   - Recent HR-initiated activity feed (last 25 commits across
 *     all 7 types, merge-sorted newest-first)
 *   - Payroll queue summary card with deep-link to
 *     /operations/payroll-candidates
 *
 * Bilingual + RTL clean — every string keyed via i18next, all
 * Latin tokens (workflow_state codes, UUID slices) wrapped in
 * <LtrText> per the M30.5.0.1 primitive contract.
 */

import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  Briefcase,
  CalendarClock,
  ClipboardEdit,
  Clock4,
  CreditCard,
  FileSignature,
  Plus,
  Skull,
  Timer,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  EmptyState,
  LtrText,
  PageHeader,
  Skeleton,
} from "../components/ui";
import { api, type HubTransactionTypeCard } from "../lib/api";

// EOS is deliberately deferred to M30.6 (Amer's refinement #2).
// This set drives the badge + the route override below.
const EOS_DEFERRED_TYPE = "eos";

// M30.5.3 frontend route override — the aggregator returns the
// backend's canonical /api path (`/operations/{type}/hr-initiate`)
// but the actual React routes live under
// `/operations/transactions/{type}/new`. Map by type so the
// "Create new" CTA hits the form page, not the API surface.
const HR_INITIATE_ROUTE_BY_TYPE: Record<string, string> = {
  leave: "/operations/transactions/leave/new",
  loan: "/operations/transactions/loan/new",
  excuse: "/operations/transactions/excuse/new",
  attendance: "/operations/transactions/attendance/new",
  overtime: "/operations/transactions/overtime/new",
  eos: "/operations/transactions/eos/new",
  contract_amendment: "/operations/transactions/contract-amendment/new",
};

// Per-transaction-type icon. The aggregator's transaction_type is
// the lookup key. New types added to the M30 enum extend this map.
const TYPE_ICONS: Record<string, LucideIcon> = {
  leave: CalendarClock,
  loan: CreditCard,
  excuse: Clock4,
  attendance: Timer,
  overtime: Timer,
  eos: Skull,
  contract_amendment: FileSignature,
};

export function OperationsHubPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.resolvedLanguage === "ar";

  const hub = useQuery({
    queryKey: ["operations-hub"],
    queryFn: () => api.operationsHub(),
    staleTime: 30_000,
  });

  return (
    <div className="mx-auto flex max-w-workspace flex-col gap-6 p-6">
      <PageHeader
        title={t("operations.hub_title")}
        subtitle={t("operations.hub_subtitle")}
      />

      {hub.isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} className="h-44 w-full" />
          ))}
        </div>
      ) : hub.isError ? (
        <div className="rounded-md border border-state-denied/30 bg-state-denied-bg/40 p-4 text-sm text-state-denied">
          {String(hub.error)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {hub.data!.per_type.map((card) => (
              <TransactionTypeCard
                key={card.transaction_type}
                card={card}
                isAr={isAr}
              />
            ))}
          </div>

          <PayrollQueueSummary
            count={hub.data!.pending_payroll_candidates}
          />

          <RecentActivity
            items={hub.data!.recent_hr_initiated_activity}
            isAr={isAr}
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

function TransactionTypeCard({
  card,
  isAr,
}: {
  card: HubTransactionTypeCard;
  isAr: boolean;
}) {
  const { t } = useTranslation();
  const Icon = TYPE_ICONS[card.transaction_type] ?? Briefcase;
  const isEosDeferred = card.transaction_type === EOS_DEFERRED_TYPE;
  const label = isAr ? card.display_ar : card.display_en;
  const createTarget =
    HR_INITIATE_ROUTE_BY_TYPE[card.transaction_type] ??
    card.create_route;

  return (
    <article className="relative flex flex-col gap-3 rounded-lg border border-border-subtle bg-surface-raised p-4 shadow-sm">
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span
            className="grid h-9 w-9 place-items-center rounded-md bg-brand-gradient text-white"
            aria-hidden
          >
            <Icon className="h-4 w-4" />
          </span>
          <h2 className="text-base font-semibold text-text-primary">
            {label}
          </h2>
        </div>
        {isEosDeferred && (
          <span className="rounded-full bg-state-paused-bg px-2 py-0.5 text-xs font-medium text-state-paused">
            {t("operations.hub_eos_coming_badge")}
          </span>
        )}
      </header>

      <dl className="grid grid-cols-2 gap-2 text-sm">
        <div className="flex flex-col rounded-md bg-state-pending-bg/40 p-2">
          <dt className="text-xs text-text-secondary">
            {t("operations.hub_card_pending_label")}
          </dt>
          <dd className="text-lg font-semibold text-state-pending">
            {card.pending_count}
          </dd>
        </div>
        <div className="flex flex-col rounded-md bg-state-approved-bg/40 p-2">
          <dt className="text-xs text-text-secondary">
            {t("operations.hub_card_recent_label")}
          </dt>
          <dd className="text-lg font-semibold text-state-approved">
            {card.recent_7d_commit_count}
          </dd>
        </div>
      </dl>

      <div className="mt-auto flex items-center gap-2">
        <Link
          to={createTarget}
          className="inline-flex flex-1 items-center justify-center gap-1 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:bg-brand-hover"
        >
          <Plus className="h-4 w-4" />
          {t("operations.hub_card_create_cta")}
        </Link>
        <Link
          to={card.detail_route}
          className="inline-flex items-center justify-center gap-1 rounded-md border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-surface-sunken"
        >
          {t("operations.hub_card_view_cta")}
          <ArrowRight className="h-4 w-4 rtl:rotate-180" />
        </Link>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Payroll queue summary
// ---------------------------------------------------------------------------

function PayrollQueueSummary({ count }: { count: number }) {
  const { t } = useTranslation();
  return (
    <Link
      to="/operations/payroll-candidates"
      className="flex items-center justify-between rounded-lg border border-border-subtle bg-surface-raised p-4 shadow-sm hover:bg-surface-sunken"
    >
      <div className="flex items-center gap-3">
        <span
          className="grid h-10 w-10 place-items-center rounded-md bg-ai-bg text-ai-deep"
          aria-hidden
        >
          <ClipboardEdit className="h-5 w-5" />
        </span>
        <div>
          <p className="text-sm font-semibold text-text-primary">
            {t("operations.hub_payroll_queue_title")}
          </p>
          <p className="text-sm text-text-secondary">
            <span className="font-semibold text-text-primary">
              {count}
            </span>{" "}
            {t("operations.hub_payroll_queue_body")}
          </p>
        </div>
      </div>
      <span className="text-sm font-medium text-brand">
        {t("operations.hub_payroll_queue_view")}{" "}
        <ArrowRight className="inline h-4 w-4 rtl:rotate-180" />
      </span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Recent activity
// ---------------------------------------------------------------------------

interface ActivityItemProps {
  transaction_type: string;
  transaction_id: string;
  employee_id: string;
  workflow_state: string | null;
  created_at: string;
}

function RecentActivity({
  items,
  isAr,
}: {
  items: ActivityItemProps[];
  isAr: boolean;
}) {
  const { t } = useTranslation();
  if (items.length === 0) {
    return (
      <EmptyState
        headline={t("operations.hub_recent_activity_title")}
        description={t("operations.hub_recent_activity_empty")}
      />
    );
  }
  return (
    <section className="overflow-hidden rounded-lg border border-border-subtle bg-surface-raised shadow-sm">
      <header className="border-b border-border-subtle p-4">
        <h2 className="text-base font-semibold text-text-primary">
          {t("operations.hub_recent_activity_title")}
        </h2>
      </header>
      <table className="w-full text-sm">
        <thead className="bg-surface-sunken text-xs uppercase text-text-secondary">
          <tr>
            <th className="px-4 py-2 text-start font-medium">
              {t("operations.hub_recent_type")}
            </th>
            <th className="px-4 py-2 text-start font-medium">
              {t("operations.hub_recent_employee")}
            </th>
            <th className="px-4 py-2 text-start font-medium">
              {t("operations.hub_recent_state")}
            </th>
            <th className="px-4 py-2 text-start font-medium">
              {t("operations.hub_recent_when")}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.transaction_id}
              className="border-t border-border-subtle"
            >
              <td className="px-4 py-2 text-text-primary">
                <LtrText>{item.transaction_type}</LtrText>
              </td>
              <td className="px-4 py-2">
                <LtrText className="text-text-secondary">
                  {item.employee_id.slice(0, 8)}
                </LtrText>
              </td>
              <td className="px-4 py-2 text-text-secondary">
                {item.workflow_state ? (
                  <LtrText>{item.workflow_state}</LtrText>
                ) : (
                  "—"
                )}
              </td>
              <td className="px-4 py-2 text-text-secondary">
                {formatWhen(item.created_at, isAr)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function formatWhen(iso: string, isAr: boolean): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(isAr ? "ar-SA" : "en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
